import { useEffect, useRef } from 'react';
import { css } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';
import { useGoogleMapsLoaded } from '@/src/lib/hooks';
import { useDarkMapStyles } from '@/src/lib/hooks/map/useDarkMapStyles';
import type { TravelMode } from './types';

type NeighborhoodMiniMapProps = {
  lat: number;
  lng: number;
  height?: string;
  interactive?: boolean;
  destination?: { lat: number; lng: number; name: string } | null;
  travelMode?: TravelMode;
};

const mapWrapper = css({
  width: '100%',
  borderRadius: 'md',
  overflow: 'hidden',
  backgroundColor: 'bg.softest',
  position: 'relative',
});

// Google Maps marker icons need resolved color strings; CSS var tokens are not applied inside Maps symbols.
const PROPERTY_MARKER_FILL_COLOR = '#c93a1a';
const PROPERTY_MARKER_STROKE_COLOR = '#ffffff';

function getGoogleTravelMode(mode: TravelMode): google.maps.TravelMode {
  const map: Record<TravelMode, google.maps.TravelMode> = {
    DRIVING: google.maps.TravelMode.DRIVING,
    WALKING: google.maps.TravelMode.WALKING,
    TRANSIT: google.maps.TravelMode.TRANSIT,
    BICYCLING: google.maps.TravelMode.BICYCLING,
  };
  return map[mode];
}

export function NeighborhoodMiniMap({
  lat,
  lng,

  height = token.var('sizes.5000'),
  interactive = false,
  destination,
  travelMode = 'DRIVING',
}: NeighborhoodMiniMapProps) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const routeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const mapsLoaded = useGoogleMapsLoaded();
  const mapStyles = useDarkMapStyles();

  useEffect(() => {
    if (!mapDivRef.current || !mapsLoaded) return;
    if (typeof google === 'undefined' || typeof google.maps?.Map !== 'function') return;
    if (mapInstanceRef.current) return;

    const map = new google.maps.Map(mapDivRef.current, {
      center: { lat, lng },
      zoom: 15,
      disableDefaultUI: true,
      gestureHandling: interactive ? 'cooperative' : 'none',
      zoomControl: interactive,
      clickableIcons: false,
    });

    new google.maps.Marker({
      position: { lat, lng },
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,

        fillColor: PROPERTY_MARKER_FILL_COLOR,
        fillOpacity: 1,

        strokeColor: PROPERTY_MARKER_STROKE_COLOR,
        strokeWeight: 2,
      },
    });

    mapInstanceRef.current = map;
  }, [lat, lng, mapsLoaded, interactive]);

  useEffect(() => {
    mapInstanceRef.current?.setOptions({ styles: mapStyles });
  }, [mapStyles, mapsLoaded]);

  // Re-center when coords change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.setCenter({ lat, lng });
  }, [lat, lng]);

  // Draw route when destination is provided
  useEffect(() => {
    const map = mapInstanceRef.current;
    routeRendererRef.current?.setMap(null);
    routeRendererRef.current = null;

    if (!map || !destination) return;

    const directionsService = new google.maps.DirectionsService();
    const renderer = new google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      preserveViewport: false,
    });
    routeRendererRef.current = renderer;

    let cancelled = false;

    directionsService.route(
      {
        origin: { lat, lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: getGoogleTravelMode(travelMode),
        ...(travelMode === 'TRANSIT' ? { transitOptions: { departureTime: new Date() } } : {}),
      },
      (result, status) => {
        if (cancelled) return;
        if (status === google.maps.DirectionsStatus.OK && result) {
          renderer.setDirections(result);
          const bounds = new google.maps.LatLngBounds();
          bounds.extend({ lat, lng });
          bounds.extend({ lat: destination.lat, lng: destination.lng });
          map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
        }
      },
    );

    return () => {
      cancelled = true;
      renderer.setMap(null);
    };
  }, [lat, lng, destination, travelMode]);

  if (!mapsLoaded) return null;

  return (
    <div className={mapWrapper} style={{ height }}>
      <div ref={mapDivRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
